import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { API } from '../../api/Api';
import Sidebar from '../components/Sidebar';
import { toast, Toaster } from 'react-hot-toast';
import { BeatLoader } from 'react-spinners';
import VisualizerDropDown from '../components/Dropdowns/VisualizerDropDown';

const EditVisualizer = () => {
    const token = localStorage.getItem('token');
    const navigate = useNavigate();
    const location = useLocation();
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        id: '',
        name: '',
        visualizertype_id: '',
        info: '',
        layout_img: null,
        layout_image_path: '',
        layout_name: '',
        parent_id: '',
        key: '',
        contact_person_name: '',
        contact_person_email: '',
        status: '1',
    });

    useEffect(() => {
        if (location.state && location.state.visualizer) {
            const visualizer = location.state.visualizer;
            setFormData({
                id: visualizer.id,
                name: visualizer.name,
                visualizertype_id: visualizer.visualizertype_id,
                info: visualizer.info,
                layout_img: null,
                layout_image_path: visualizer.layout_image,
                layout_name: visualizer.layout_name,
                parent_id: visualizer.parent_id,
                key: visualizer.key,
                contact_person_name: visualizer.contact_person_name,
                contact_person_email: visualizer.contact_person_email,
                status: visualizer.status ? '1' : '0',
            });
        }
    }, [location.state]);


    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        setErrors((prev) => ({ ...prev, [name]: '' }));

        if (type === 'file') {
            const file = files[0];
            if (file) {
                const validTypes = ['image/png', 'image/webp'];
                if (!validTypes.includes(file.type)) {
                    setErrors((prev) => ({ ...prev, [name]: 'Only PNG and WebP files are allowed' }));
                    return;
                }
            }
            setFormData((prevData) => ({ ...prevData, [name]: file }));
        } else {
            setFormData((prevData) => ({ ...prevData, [name]: value }));
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!formData.name) newErrors.name = 'Name is required';
        if (!formData.visualizertype_id) newErrors.visualizertype_id = 'Visualizer type is required';
        if (!formData.info) newErrors.info = 'Info is required';
        if (!formData.layout_name) newErrors.layout_name = 'Layout Name is required';
        if (!formData.contact_person_name) newErrors.contact_person_name = 'Contact person name is required';
        if (!formData.contact_person_email) newErrors.contact_person_email = 'Contact person email is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);

        const url = `${process.env.REACT_APP_API_URL}/visualizer/createupdatevisualizer`;

        const formDataToSend = new FormData();

        formDataToSend.append('id', formData.id || '');
        formDataToSend.append('name', formData.name);
        formDataToSend.append('visualizertype_id', formData.visualizertype_id);
        formDataToSend.append('info', formData.info);

        if (formData.layout_img && formData.layout_img instanceof File) {
            // New image uploaded
            formDataToSend.append('layout_image', formData.layout_img);
            formDataToSend.append('layout_image_path', '');
        } else {
            // No new image uploaded — keep the existing path
            formDataToSend.append('layout_image', '');
            formDataToSend.append('layout_image_path', formData.layout_image_path || '');
        }



        formDataToSend.append('layout_name', formData.layout_name);
        formDataToSend.append('parent_id', formData.parent_id || '');
        formDataToSend.append('key', formData.key || '');
        formDataToSend.append('contact_person_name', formData.contact_person_name);
        formDataToSend.append('contact_person_email', formData.contact_person_email);
        formDataToSend.append('status', formData.status ?? 1);



        try {
            const response = await API(url, formDataToSend, token, 'multipart/form-data');
            if (response?.Result === 1) {
                toast.success('Visualizer updated successfully!');
                navigate('/admin/layouts');
            } else {
                toast.error('Failed to update visualizer.');
            }
        } catch (err) {
            console.error(err);
            toast.error('Something went wrong!');
        } finally {
            setLoading(false);
        }
    };




    const handleTypeSelect = (visualizertype_id) => {
        setFormData((prevData) => ({
            ...prevData,
            visualizertype_id: visualizertype_id || "",
        }));
    };

    return (
        <div className="flex h-screen fixed w-full bg-gray-100">
            <div className="md:w-64 w-16 bg-gray-800 transition-all">
                <Sidebar />
            </div>

            {loading && (
                <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-md bg-black/40">
                    <BeatLoader color="#14b8a6" size={20} />
                </div>
            )}

            <Toaster position="top-center" />

            <div className="flex flex-col flex-1">
                <div className="bg-gray-100 flex-1 p-6 overflow-auto">
                    <div className="bg-white rounded-2xl w-full shadow-lg">
                        <div className="flex justify-between items-center px-6 pt-6">
                            <div>
                                <p className="text-teal-500 text-2xl font-semibold">Edit Visualizer</p>
                                <p className="text-gray-600 text-sm">
                                    <Link to="/admin/layouts" className="text-teal-500 hover:underline">Visualizer</Link> / Edit Visualizer
                                </p>
                            </div>
                            <button
                                className="bg-gradient-to-r from-teal-400 to-teal-500 text-white rounded-lg px-6 py-2 hover:from-teal-500 hover:to-teal-600 transition shadow-md"
                                onClick={() => navigate('/admin/layouts')}
                            >
                                Back to List
                            </button>
                        </div>
                        <hr className="border-gray-200 my-4" />

                        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto text-xs md:text-base my-4 gap-6 w-[70%] m-auto">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
                                <div className="relative z-0 w-full mb-1 group">
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={`block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer ${errors.name ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder=" "
                                    />
                                    <label
                                        htmlFor="name"
                                        className={`absolute text-xs md:text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] transition-all ease-in-out
                                            ${formData.name ? 'peer-focus:text-blue-600' : 'peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0'}
                                            peer-focus:-translate-y-6 peer-focus:scale-75`}
                                    >
                                        Name <span className="text-red-500">*</span>
                                    </label>
                                    {errors.name && <p className="text-red-500 text-xs italic">{errors.name}</p>}
                                </div>

                                <div className="relative z-0 w-full mb-1 group">
                                    <VisualizerDropDown
                                        value={formData.visualizertype_id}
                                        onTypeSelect={handleTypeSelect}
                                        name="visualizertype_id"
                                        className={`block py-3 mt- px-0 w-full text-sm text-gray-900 bg-transparent border-b border-gray-300 focus:outline-none focus:ring-0 focus:border-blue-600 peer`}
                                    />
                                    <label
                                        htmlFor="visualizertype_id"
                                        className={`absolute text-xs md:text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-1 -z-10 origin-[0] transition-all ease-in-out
                                            ${formData.visualizertype_id ? 'peer-focus:text-blue-600' : 'peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0'}
                                            peer-focus:-translate-y-6 peer-focus:scale-75`}
                                    >
                                        Visualizer Type <span className="text-red-500">*</span>
                                    </label>
                                    {errors.visualizertype_id && <p className="text-red-500 text-xs italic">{errors.visualizertype_id}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-1 gap-6 p-6">
                                <div className="relative z-0 w-full mb-1 group">
                                    <textarea
                                        id="info"
                                        name="info"
                                        value={formData.info}
                                        onChange={handleChange}
                                        className={`block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer ${errors.info ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder=" "
                                    />
                                    <label
                                        htmlFor="info"
                                        className={`absolute text-xs md:text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] transition-all ease-in-out
                                            ${formData.info ? 'peer-focus:text-blue-600' : 'peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0'}
                                            peer-focus:-translate-y-6 peer-focus:scale-75`}
                                    >
                                        Info <span className="text-red-500">*</span>
                                    </label>
                                    {errors.info && <p className="text-red-500 text-xs italic">{errors.info}</p>}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
                                <div className="relative z-0 w-full mb-1 group">
                                    <input
                                        type="text"
                                        id="layout_name"
                                        name="layout_name"
                                        value={formData.layout_name}
                                        onChange={handleChange}
                                        className={`block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer ${errors.layout_name ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder=" "
                                    />
                                    <label
                                        htmlFor="layout_name"
                                        className={`absolute text-xs md:text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] transition-all ease-in-out
                                            ${formData.layout_name ? 'peer-focus:text-blue-600' : 'peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0'}
                                            peer-focus:-translate-y-6 peer-focus:scale-75`}
                                    >
                                        Layout Name <span className="text-red-500">*</span>
                                    </label>
                                    {errors.layout_name && <p className="text-red-500 text-xs italic">{errors.layout_name}</p>}
                                </div>

                                <div className="relative z-0 w-full mb-1 group">
                                    {formData.layout_image_path && (
                                        <div className="mb-4">
                                            <img
                                                src={formData.layout_image_path}
                                                alt="Current Layout"
                                                className="w-28 h-auto max-h-48 object-contain mb-4"
                                            />
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        id="layout_img"
                                        accept=".png,.webp"
                                        name="layout_img"
                                        onChange={handleChange}
                                        className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
                                    />
                                    <label
                                        htmlFor="layout_img"
                                        className={`absolute text-xs md:text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] transition-all ease-in-out
            ${formData.layout_img ? 'peer-focus:text-blue-600' : 'peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0'}
            peer-focus:-translate-y-6 peer-focus:scale-75`}
                                    >
                                        Layout Image <span className="text-red-500">*</span>
                                    </label>
                                    {errors.layout_img && <p className="text-red-500 text-xs italic">{errors.layout_img}</p>}
                                </div>


                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
                                <div className="relative z-0 w-full mb-1 group">
                                    <input
                                        type="text"
                                        id="contact_person_name"
                                        name="contact_person_name"
                                        value={formData.contact_person_name}
                                        onChange={handleChange}
                                        className={`block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer ${errors.contact_person_name ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder=" "
                                    />
                                    <label
                                        htmlFor="contact_person_name"
                                        className={`absolute text-xs md:text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] transition-all ease-in-out
                                            ${formData.contact_person_name ? 'peer-focus:text-blue-600' : 'peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0'}
                                            peer-focus:-translate-y-6 peer-focus:scale-75`}
                                    >
                                        Contact Person Name <span className="text-red-500">*</span>
                                    </label>
                                    {errors.contact_person_name && <p className="text-red-500 text-xs italic">{errors.contact_person_name}</p>}
                                </div>

                                <div className="relative z-0 w-full mb-1 group">
                                    <input
                                        type="email"
                                        id="contact_person_email"
                                        name="contact_person_email"
                                        value={formData.contact_person_email}
                                        onChange={handleChange}
                                        className={`block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer ${errors.contact_person_email ? 'border-red-500' : 'border-gray-300'}`}
                                        placeholder=" "
                                    />
                                    <label
                                        htmlFor="contact_person_email"
                                        className={`absolute text-xs md:text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] transition-all ease-in-out
                                            ${formData.contact_person_email ? 'peer-focus:text-blue-600' : 'peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0'}
                                            peer-focus:-translate-y-6 peer-focus:scale-75`}
                                    >
                                        Contact Person Email <span className="text-red-500">*</span>
                                    </label>
                                    {errors.contact_person_email && <p className="text-red-500 text-xs italic">{errors.contact_person_email}</p>}
                                </div>
                            </div>

                            <div className="flex items-center space-x-2 m-4 p-6">
                                <input
                                    type="checkbox"
                                    id="status"
                                    checked={formData.status === '1'}
                                    onChange={(e) => setFormData(prev => ({
                                        ...prev,
                                        status: e.target.checked ? '1' : '0',
                                    }))}
                                    className="mr-2"
                                />
                                <label htmlFor="status">Active</label>
                            </div>

                            <div className="col-span-2 flex justify-end mt-4 p-6">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="bg-gradient-to-r from-teal-400 to-teal-500 text-white rounded-lg px-6 py-2 hover:from-teal-500 hover:to-teal-600 transition shadow-md"
                                >
                                    {loading ? 'Updating...' : 'Update'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditVisualizer;
